import React, { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';

const HowSection = styled.section`
  padding: 5rem 2rem;
  max-width: 1000px;
  margin: 0 auto;

  @media (max-width: 768px) {
    padding: 3rem 1rem;
  }
`;

const SectionHeader = styled.div`
  text-align: center;
  margin-bottom: 3rem;

  opacity: 0;
  transform: translateY(40px);
  transition: opacity 0.8s ease-out, transform 0.8s ease-out;

  &.visible {
    opacity: 1;
    transform: translateY(0);
  }
`;

const SectionLabel = styled.span`
  display: inline-block;
  padding: 0.5rem 1.25rem;
  background: ${({ theme }) => theme.colors.activeColor}20;
  color: ${({ theme }) => theme.colors.activeColor};
  font-size: 0.75rem;
  font-weight: 600;
  letter-spacing: 1.5px;
  text-transform: uppercase;
  border-radius: 50px;
  margin-bottom: 1.5rem;
`;

const SectionTitle = styled.h2`
  font-size: clamp(2rem, 4vw, 3rem);
  font-weight: 700;
  color: ${({ theme }) => theme.colors.headline};

  @media (max-width: 768px) {
    font-size: 1.75rem;
  }
`;

const AccentText = styled.span`
  color: ${({ theme }) => theme.colors.activeColor};
`;

const StepsContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 3rem;
`;

const Step = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 2rem;

  opacity: 0;
  transform: translateX(-40px);
  transition: opacity 0.6s ease-out, transform 0.6s ease-out;

  &.visible {
    opacity: 1;
    transform: translateX(0);
  }

  &:nth-child(1) { transition-delay: 0s; }
  &:nth-child(2) { transition-delay: 0.15s; }
  &:nth-child(3) { transition-delay: 0.3s; }

  @media (max-width: 768px) {
    flex-direction: column;
    text-align: center;
    align-items: center;
    transform: translateY(40px);

    &.visible {
      transform: translateY(0);
    }
  }
`;

const StepNumber = styled.div`
  width: 70px;
  height: 70px;
  min-width: 70px;
  background: ${({ theme }) => theme.colors.activeColor};
  color: white;
  font-size: 1.75rem;
  font-weight: 700;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 4px 15px ${({ theme }) => theme.colors.activeColor}40;
`;

const StepContent = styled.div`
  flex: 1;
  padding-top: 0.5rem;
`;

const StepTitle = styled.h3`
  font-size: 1.5rem;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.headline};
  margin-bottom: 0.75rem;
`;

const StepDescription = styled.p`
  font-size: 1.1rem;
  color: ${({ theme }) => theme.colors.paragraph};
  line-height: 1.7;
`;

const steps = [
  {
    number: 1,
    title: 'Set Your Target Markets',
    description: "Add one or more ZIP codes. We pull every active single-family listing from the MLS \u2014 typically 20\u2013100+ properties per ZIP."
  },
  {
    number: 2,
    title: 'Watch Deals Get Analyzed',
    description: 'One query analyzes every listing at once. AI-estimated rent, local tax rates, insurance, and complete CapEx reserves based on property age and condition.'
  },
  {
    number: 3,
    title: 'Find What Cash Flows',
    description: 'Properties that hit your COC threshold show COC%, IRR, 5-year amortization, and valuation. Drill into any deal for the full breakdown.'
  }
];

const HowItWorks = () => {
  const sectionRef = useRef(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.unobserve(entry.target);
        }
      },
      { threshold: 0.05 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    const fallback = setTimeout(() => setIsVisible(true), 2000);

    return () => {
      observer.disconnect();
      clearTimeout(fallback);
    };
  }, []);

  return (
    <HowSection ref={sectionRef}>
      <SectionHeader className={isVisible ? 'visible' : ''}>
        <SectionLabel>How It Works</SectionLabel>
        <SectionTitle>
          From ZIP Code to <AccentText>Full Breakdown</AccentText> in About 30 Seconds
        </SectionTitle>
      </SectionHeader>
      <StepsContainer>
        {steps.map((step) => (
          <Step key={step.number} className={isVisible ? 'visible' : ''}>
            <StepNumber>{step.number}</StepNumber>
            <StepContent>
              <StepTitle>{step.title}</StepTitle>
              <StepDescription>{step.description}</StepDescription>
            </StepContent>
          </Step>
        ))}
      </StepsContainer>
    </HowSection>
  );
};

export default HowItWorks;
